import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { TenantBrandingProvider } from './contexts/TenantBrandingContext';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { ProtectedRoute, AdminRoute } from './components/auth/ProtectedRoute';
import { DISABLED_FEATURES } from './config/api';
import SectionGuard from './components/SectionGuard';
import './App.css';

// Pages
import LandingPage from './pages/LandingPage';
import TenantLandingPage from './pages/TenantLandingPage';
import LoginPage from './pages/LoginPage';
// Removed: RegisterPage, ForgotPasswordPage - simplified login flow
import DashboardPage from './pages/DashboardPage';
import CourseCatalogPage from './pages/CourseCatalogPage';
import CourseDetailPage from './pages/CourseDetailPage';
import CoursePlayer from './pages/CoursePlayer';
import SCORMPlayer from './pages/SCORMPlayer';
import QuizPlayer from './pages/QuizPlayer';
import WebinarsPage from './pages/WebinarsPage';
import EBooksPage from './pages/EBooksPage';
import EBookDetailPage from './pages/EBookDetailPage';
import CertificatesPage from './pages/CertificatesPage';
import CampaignsPage from './pages/CampaignsPage';
import { LeaderboardPage } from './components/Leaderboard';
import AdminDashboard from './pages/AdminDashboard';
import AICourseGenerator from './pages/AICourseGenerator';
import AICoursePlayer from './pages/AICoursePlayer';
import CourseBuilder from './pages/CourseBuilder';
import InstructorAssessments from './pages/InstructorAssessments';
import LearningPathwaysPage from './pages/LearningPathwaysPage';
import PathwayProgressPage from './pages/PathwayProgressPage';
import MasterclassDetailPage from './pages/MasterclassDetailPage';
import WishlistPage from './pages/WishlistPage';
import WSPPage from './pages/WSPPage';
import SettingsPage from './pages/SettingsPage';
import AboutPage from './pages/AboutPage';
import FAQPage from './pages/FAQPage';
import PolicyPage from './pages/PolicyPage';
import AuthCallback from './pages/AuthCallback';
import SiteAccessGate from './components/SiteAccessGate';

// Learning Lab Pages
import LearningLabCatalog from './pages/LearningLabCatalog';
import LearningLabPlayer from './pages/LearningLabPlayer';
import LearningLabBuilder from './pages/admin/LearningLabBuilder';
import LearningLabBuilderV2 from './pages/admin/LearningLabBuilderV2';
import LearningLabPlayerV2 from './pages/LearningLabPlayerV2';
import AILabGenerator from './pages/admin/AILabGenerator';
import ExternalLearningPlayer from './pages/ExternalLearningPlayer';
import ExternalCoursePlayer from './pages/ExternalCoursePlayer';
import LTILaunch from './pages/LTILaunch';
import AcademyPage from './pages/AcademyPage';
import RegulatoryAlertsPage from './pages/RegulatoryAlertsPage';
import EnterpriseSSOPage from './pages/EnterpriseSSOPage';
import SSOCallbackPage from './pages/SSOCallbackPage';
import CompetencyFrameworkGenerator from './pages/CompetencyFrameworkGenerator';
// Professional Programmes Pages
import ProgrammesPage from './pages/ProgrammesPage';
import ProgrammeDetailPage from './pages/ProgrammeDetailPage';
import ProgrammeBuilderPage from './pages/admin/ProgrammeBuilderPage';
import ProgrammeWorkflowPage from './pages/admin/ProgrammeWorkflowPage';
import ProgrammeLearnersPage from './pages/admin/ProgrammeLearnersPage';
import ProgrammeGradebookPage from './pages/admin/ProgrammeGradebookPage';
// Removed: PlatformAdminLogin - using admin access token instead

// Wrapper to handle OAuth callback
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <SiteAccessGate>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<TenantLandingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/signup" element={<Navigate to="/login" replace />} />
      <Route path="/responsible-ai" element={<PolicyPage />} />
      <Route path="/privacy-policy" element={<PolicyPage />} />
      <Route path="/terms-of-use" element={<PolicyPage />} />
      <Route path="/hitl-governance" element={<PolicyPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/enterprise-sso" element={<EnterpriseSSOPage />} />
      <Route path="/sso/callback" element={<SSOCallbackPage />} />
      <Route path="/auth/callback" element={<Navigate to="/login" replace />} />
      
      {/* Navigate Learning Academy - Public ICA Course Catalog */}
      {/* TEMPORARILY DISABLED - see DISABLED_FEATURES in config/api.js */}
      <Route path="/academy" element={
        <SectionGuard section="navigate_academy">
        {DISABLED_FEATURES.navigate_academy ? <Navigate to="/courses" replace /> : <AcademyPage />}
        </SectionGuard>
      } />
      
      {/* Navigate Learning - Regulatory Alerts */}
      {/* TEMPORARILY DISABLED - see DISABLED_FEATURES in config/api.js */}
      <Route path="/regulatory-alerts" element={
        <SectionGuard section="regulatory_alerts">
        {DISABLED_FEATURES.regulatory_alerts ? <Navigate to="/dashboard" replace /> : <RegulatoryAlertsPage />}
        </SectionGuard>
      } />
      
      {/* Course Routes (Public viewing, protected enrollment) */}
      <Route path="/courses" element={<SectionGuard section="courses"><CourseCatalogPage /></SectionGuard>} />
      <Route path="/courses/:courseId" element={<SectionGuard section="courses"><CourseDetailPage /></SectionGuard>} />
      <Route path="/courses/:courseId/learn" element={
        <SectionGuard section="courses">
        <ProtectedRoute>
          <CoursePlayer />
        </ProtectedRoute>
        </SectionGuard>
      } />
      <Route path="/scorm/:packageId" element={
        <ProtectedRoute>
          <SCORMPlayer />
        </ProtectedRoute>
      } />
      <Route path="/quiz/:quizId" element={
        <ProtectedRoute>
          <QuizPlayer />
        </ProtectedRoute>
      } />
      
      {/* Masterclass & eBook Routes (Public) */}
      <Route path="/masterclasses" element={<SectionGuard section="masterclasses"><WebinarsPage /></SectionGuard>} />
      <Route path="/masterclasses/:webinarId" element={<SectionGuard section="masterclasses"><MasterclassDetailPage /></SectionGuard>} />
      <Route path="/ebooks" element={<SectionGuard section="ebooks"><EBooksPage /></SectionGuard>} />
      <Route path="/ebooks/:ebookId" element={<SectionGuard section="ebooks"><EBookDetailPage /></SectionGuard>} />
      <Route path="/learning-pathways" element={<SectionGuard section="learning_pathways"><LearningPathwaysPage /></SectionGuard>} />
      <Route path="/pathways/:pathwayId" element={
        <SectionGuard section="learning_pathways">
        <ProtectedRoute>
          <PathwayProgressPage />
        </ProtectedRoute>
        </SectionGuard>
      } />
      <Route path="/pathways" element={<SectionGuard section="learning_pathways"><LearningPathwaysPage /></SectionGuard>} />
      
      {/* WSP Route (Protected) */}
      <Route path="/wsp" element={
        <SectionGuard section="wsp">
        <ProtectedRoute>
          <WSPPage />
        </ProtectedRoute>
        </SectionGuard>
      } />
      
      {/* Settings Route (Protected) */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      {/* Competency Framework Generator (Replaces Global Role Readiness) */}
      <Route path="/competency-framework" element={
        <ProtectedRoute>
          <CompetencyFrameworkGenerator />
        </ProtectedRoute>
      } />
      
      {/* Professional Programmes Routes */}
      <Route path="/programmes" element={<SectionGuard section="programmes"><ProgrammesPage /></SectionGuard>} />
      <Route path="/programmes/:programmeId" element={<SectionGuard section="programmes"><ProgrammeDetailPage /></SectionGuard>} />
      
      {/* Admin Programme Management Routes */}
      <Route path="/admin/programmes/create" element={
        <AdminRoute>
          <ProgrammeWorkflowPage />
        </AdminRoute>
      } />
      <Route path="/admin/programmes/:programmeId/workflow" element={
        <AdminRoute>
          <ProgrammeWorkflowPage />
        </AdminRoute>
      } />
      <Route path="/admin/programmes/:programmeId/edit" element={
        <AdminRoute>
          <ProgrammeBuilderPage />
        </AdminRoute>
      } />
      <Route path="/admin/programmes/:programmeId/builder" element={
        <AdminRoute>
          <ProgrammeBuilderPage />
        </AdminRoute>
      } />
      <Route path="/admin/programmes/:programmeId/learners" element={
        <AdminRoute>
          <ProgrammeLearnersPage />
        </AdminRoute>
      } />
      <Route path="/admin/programmes/:programmeId/gradebook" element={
        <AdminRoute>
          <ProgrammeGradebookPage />
        </AdminRoute>
      } />
      
      {/* Learning Lab Routes */}
      <Route path="/labs" element={<SectionGuard section="learning_labs"><LearningLabCatalog /></SectionGuard>} />
      <Route path="/labs/:labId" element={
        <SectionGuard section="learning_labs">
        <ProtectedRoute>
          <LearningLabPlayer />
        </ProtectedRoute>
        </SectionGuard>
      } />
      
      {/* Learning Lab V2 Routes - Multi-scenario Labs */}
      <Route path="/labs-v2/:labId" element={
        <SectionGuard section="learning_labs">
        <ProtectedRoute>
          <LearningLabPlayerV2 />
        </ProtectedRoute>
        </SectionGuard>
      } />
      
      {/* External Learning Routes (SCORM Cloud / Third-party content) */}
      <Route path="/external-learning" element={<Navigate to="/courses?tab=external" replace />} />
      <Route path="/external-learning/:moduleId" element={
        <SectionGuard section="external_courses">
        <ProtectedRoute>
          <ExternalLearningPlayer />
        </ProtectedRoute>
        </SectionGuard>
      } />
      
      {/* External Courses (Old Mutual / Iframe-based) */}
      <Route path="/external-courses/:courseId" element={
        <SectionGuard section="external_courses">
        <ProtectedRoute>
          <ExternalCoursePlayer />
        </ProtectedRoute>
        </SectionGuard>
      } />
      
      {/* LTI Launch Route (from Moodle) */}
      <Route path="/lti/launch" element={<LTILaunch />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/my-courses" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/wishlist" element={
        <SectionGuard section="wishlists">
        <ProtectedRoute>
          <WishlistPage />
        </ProtectedRoute>
        </SectionGuard>
      } />
      <Route path="/certificates" element={
        <SectionGuard section="certificates">
          <ProtectedRoute>
            <CertificatesPage />
          </ProtectedRoute>
        </SectionGuard>
      } />
      <Route path="/campaigns" element={
        <SectionGuard section="campaigns">
        <ProtectedRoute>
          <CampaignsPage />
        </ProtectedRoute>
        </SectionGuard>
      } />
      <Route path="/leaderboard" element={
        <SectionGuard section="leaderboard">
        <ProtectedRoute>
          <LeaderboardPage />
        </ProtectedRoute>
        </SectionGuard>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/cpd" element={
        <SectionGuard section="cpd_tracking">
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
        </SectionGuard>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <AdminRoute>
          <AdminDashboard />
        </AdminRoute>
      } />
      <Route path="/admin/*" element={
        <AdminRoute>
          <AdminDashboard />
        </AdminRoute>
      } />
      
      {/* AI Course Generator Routes */}
      <Route path="/ai-course/create" element={
        <AdminRoute>
          <AICourseGenerator />
        </AdminRoute>
      } />
      <Route path="/courses/:courseId/ai-learn" element={
        <SectionGuard section="courses">
        <ProtectedRoute>
          <AICoursePlayer />
        </ProtectedRoute>
        </SectionGuard>
      } />
      
      {/* Course Builder Routes */}
      <Route path="/admin/course-builder" element={
        <AdminRoute>
          <CourseBuilder />
        </AdminRoute>
      } />
      <Route path="/admin/course-builder/:courseId" element={
        <AdminRoute>
          <CourseBuilder />
        </AdminRoute>
      } />
      
      {/* Learning Lab Builder Routes */}
      <Route path="/admin/labs" element={
        <AdminRoute>
          <LearningLabBuilder />
        </AdminRoute>
      } />
      <Route path="/admin/labs/:labId" element={
        <AdminRoute>
          <LearningLabBuilder />
        </AdminRoute>
      } />
      
      {/* Learning Lab V2 Builder Routes - Multi-scenario Labs */}
      <Route path="/admin/labs-v2" element={
        <AdminRoute>
          <LearningLabBuilderV2 />
        </AdminRoute>
      } />
      <Route path="/admin/labs-v2/create" element={
        <AdminRoute>
          <LearningLabBuilderV2 />
        </AdminRoute>
      } />
      <Route path="/admin/labs-v2/:labId/edit" element={
        <AdminRoute>
          <LearningLabBuilderV2 />
        </AdminRoute>
      } />
      
      {/* AI Lab Generator */}
      <Route path="/admin/labs-v2/ai-generate" element={
        <AdminRoute>
          <AILabGenerator />
        </AdminRoute>
      } />
      
      {/* NAVI Instructor Assessments */}
      <Route path="/admin/navi-assessments" element={
        <AdminRoute>
          <InstructorAssessments />
        </AdminRoute>
      } />
      
      {/* Enterprise Tenant Management */}
      <Route path="/admin/enterprise-tenants" element={<Navigate to="/admin?tab=sso" replace />} />
      
      {/* Payments are disabled - all courses are free */}
      {['/payment/*', '/pricing', '/subscription/*', '/checkout'].map((p) => (
        <Route key={p} path={p} element={<Navigate to="/dashboard" replace />} />
      ))}
      
      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </SiteAccessGate>
  );
}


// 404 Page
function NotFoundPage() {
  return (
    <div className="min-h-screen bg-navigate-dark flex items-center justify-center px-4">
      <div className="text-center">
        <h1 className="font-heading text-6xl font-bold text-navigate-gold mb-4">404</h1>
        <p className="text-xl text-white mb-2">Page Not Found</p>
        <p className="text-zinc-400 mb-8">
          The page you are looking for does not exist.
        </p>
        <a href="/" className="btn-gold inline-block">
          Go Home
        </a>
      </div>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <TenantProvider>
          <AuthProvider>
            <TenantBrandingProvider>
              <FeatureFlagsProvider>
                <AppRouter />
                <Toaster 
                  position="top-right"
                  toastOptions={{
                    style: {
                      background: '#18181b',
                      border: '1px solid #27272a',
                      color: '#fafafa',
                    },
                  }}
                />
              </FeatureFlagsProvider>
            </TenantBrandingProvider>
          </AuthProvider>
        </TenantProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
